(() => {
  const browsers = new WeakSet();
  const galleryCache = new Map();

  const normaliseImageUrl = (value) => {
    if (!value) return '';

    try {
      return new URL(
        value,
        window.location.origin
      ).href;
    } catch (error) {
      return '';
    }
  };

  const comparableImageUrl = (value) => {
    const imageUrl =
      normaliseImageUrl(value);

    if (!imageUrl) return '';

    try {
      const url = new URL(imageUrl);

      url.searchParams.delete('width');
      url.searchParams.delete('height');
      url.searchParams.delete('crop');

      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return imageUrl;
    }
  };

  const sizedImageUrl = (
    value,
    width
  ) => {
    const imageUrl =
      normaliseImageUrl(value);

    if (!imageUrl) return '';

    try {
      const url = new URL(imageUrl);

      url.searchParams.set(
        'width',
        String(width)
      );

      return url.href;
    } catch (error) {
      return imageUrl;
    }
  };

  const getProductJsonUrl = (
    productHref
  ) => {
    const productUrl = new URL(
      productHref,
      window.location.origin
    );

    productUrl.hash = '';
    productUrl.search = '';

    productUrl.pathname =
      productUrl.pathname
        .replace(/\.js$/, '')
        .replace(/\/$/, '') + '.js';

    return productUrl.href;
  };

  const loadProductGallery = (
    productHref
  ) => {
    const requestUrl =
      getProductJsonUrl(productHref);

    if (!galleryCache.has(requestUrl)) {
      const galleryRequest = fetch(
        requestUrl,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Unable to load product gallery: ${response.status}`
            );
          }

          return response.json();
        })
        .then((product) => {
          const images =
            Array.isArray(product.images)
              ? product.images
              : [];

          const uniqueImages = [];
          const imageKeys = new Set();

          images.forEach((image) => {
            const source =
              typeof image === 'string'
                ? image
                : image?.src || '';

            const normalisedSource =
              normaliseImageUrl(source);

            const imageKey =
              comparableImageUrl(
                normalisedSource
              );

            if (
              !normalisedSource ||
              !imageKey ||
              imageKeys.has(imageKey)
            ) {
              return;
            }

            imageKeys.add(imageKey);

            uniqueImages.push({
              src: normalisedSource,
              alt:
                typeof image ===
                  'object' &&
                image?.alt
                  ? image.alt
                  : product.title || ''
            });
          });

          return {
            title: product.title || '',
            images: uniqueImages
          };
        })
        .catch((error) => {
          galleryCache.delete(requestUrl);
          throw error;
        });

      galleryCache.set(
        requestUrl,
        galleryRequest
      );
    }

    return galleryCache.get(requestUrl);
  };

  const initialiseGallery = (browser) => {
    if (
      !browser ||
      browsers.has(browser)
    ) {
      return;
    }

    const viewer = browser.querySelector(
      '[data-list-viewer]'
    );

    const viewerContent =
      browser.querySelector(
        '[data-list-viewer-content]'
      );

    const imageLink =
      browser.querySelector(
        '.the-list__image-link'
      );

    const imageShell =
      browser.querySelector(
        '[data-list-image-shell]'
      );

    const primaryImage =
      browser.querySelector(
        '[data-list-image]'
      );

    if (
      !viewer ||
      !viewerContent ||
      !imageLink ||
      !imageShell ||
      !primaryImage
    ) {
      return;
    }

    browsers.add(browser);

    const carousel =
      document.createElement('div');

    carousel.className =
      'the-list__carousel';

    carousel.dataset.listCarousel = '';

    imageLink.before(carousel);
    carousel.append(imageLink);

    const track =
      document.createElement('span');

    track.className =
      'the-list__carousel-track';

    track.dataset.listCarouselTrack = '';

    const primarySlide =
      document.createElement('span');

    primarySlide.className =
      'the-list__carousel-slide';

    primarySlide.dataset.listCarouselSlide =
      '';

    primaryImage.before(track);
    primarySlide.append(primaryImage);
    track.append(primarySlide);

    primaryImage.draggable = false;

    const previousButton =
      document.createElement('button');

    previousButton.type = 'button';

    previousButton.className =
      'the-list__carousel-arrow the-list__carousel-arrow--previous';

    previousButton.dataset
      .listCarouselPrevious = '';

    previousButton.setAttribute(
      'aria-label',
      'Previous product image'
    );

    previousButton.innerHTML = `
      <svg
        aria-hidden="true"
        viewBox="0 0 12 20"
        focusable="false"
      >
        <path
          d="M10 2 2 10l8 8"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        />
      </svg>
    `;

    const nextButton =
      document.createElement('button');

    nextButton.type = 'button';

    nextButton.className =
      'the-list__carousel-arrow the-list__carousel-arrow--next';

    nextButton.dataset
      .listCarouselNext = '';

    nextButton.setAttribute(
      'aria-label',
      'Next product image'
    );

    nextButton.innerHTML = `
      <svg
        aria-hidden="true"
        viewBox="0 0 12 20"
        focusable="false"
      >
        <path
          d="m2 2 8 8-8 8"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        />
      </svg>
    `;

    const dots =
      document.createElement('div');

    dots.className =
      'the-list__carousel-dots';

    dots.dataset.listCarouselDots = '';

    dots.setAttribute(
      'aria-label',
      'Product image selector'
    );

    carousel.append(
      previousButton,
      nextButton,
      dots
    );

    let activeIndex = 0;
    let galleryLength = 1;
    let galleryVersion = 0;
    let scrollFrame = null;
    let pendingRefresh = false;
    let pointerStartX = null;
    let suppressNextLinkClick = false;

    const getSlides = () => {
      return Array.from(
        track.querySelectorAll(
          '[data-list-carousel-slide]'
        )
      );
    };

    const updateControls = () => {
      const hasMultipleImages =
        galleryLength > 1;

      carousel.classList.toggle(
        'has-multiple-images',
        hasMultipleImages
      );

      previousButton.hidden =
        !hasMultipleImages;

      nextButton.hidden =
        !hasMultipleImages;

      dots.hidden =
        !hasMultipleImages;

      previousButton.disabled =
        activeIndex <= 0;

      nextButton.disabled =
        activeIndex >=
        galleryLength - 1;

      Array.from(dots.children).forEach(
        (dot, index) => {
          const isActive =
            index === activeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-current',
            isActive
              ? 'true'
              : 'false'
          );
        }
      );
    };

    const goToSlide = (
      requestedIndex,
      behaviour = 'smooth'
    ) => {
      const slides = getSlides();

      const finalIndex = Math.max(
        0,
        Math.min(
          requestedIndex,
          slides.length - 1
        )
      );

      const targetSlide =
        slides[finalIndex];

      if (!targetSlide) return;

      const leadingPadding =
        Number.parseFloat(
          getComputedStyle(
            track
          ).paddingLeft
        ) || 0;

      activeIndex = finalIndex;

      track.scrollTo({
        left: Math.max(
          0,
          targetSlide.offsetLeft -
            leadingPadding
        ),
        behavior: behaviour
      });

      updateControls();
    };

    const clearAdditionalSlides = () => {
      getSlides()
        .slice(1)
        .forEach((slide) => {
          slide.remove();
        });

      activeIndex = 0;
      galleryLength = 1;

      track.scrollTo({
        left: 0,
        behavior: 'auto'
      });

      dots.replaceChildren();
      updateControls();
    };

    const createDots = (length) => {
      const fragment =
        document.createDocumentFragment();

      for (
        let index = 0;
        index < length;
        index += 1
      ) {
        const dot =
          document.createElement('button');

        dot.type = 'button';

dot.className =
  'the-list__carousel-position-dot';

        dot.setAttribute(
          'aria-label',
          `Show product image ${
            index + 1
          }`
        );

        dot.addEventListener(
          'click',
          () => {
            goToSlide(index);
          }
        );

        fragment.append(dot);
      }

      dots.replaceChildren(fragment);
    };

    const renderGallery = (
      gallery,
      expectedMainImage
    ) => {
      clearAdditionalSlides();

const mainImageKey =
  comparableImageUrl(
    expectedMainImage
  );

const uniqueImages = [
  {
    src: expectedMainImage,
    alt:
      primaryImage.alt ||
      gallery.title ||
      ''
  }
];

const renderedKeys = new Set([
  mainImageKey
]);

gallery.images.forEach(
  (image, index) => {
    /*
     * Shopify returns the featured image first.
     * That image is already displayed by primaryImage,
     * so it must not be added for a second time.
     */
    if (index === 0) return;

    const imageKey =
      comparableImageUrl(image.src);

    if (
      !imageKey ||
      renderedKeys.has(imageKey)
    ) {
      return;
    }

    renderedKeys.add(imageKey);
    uniqueImages.push(image);
  }
);

      uniqueImages
        .slice(1)
        .forEach((image, index) => {
          const slide =
            document.createElement('span');

          slide.className =
            'the-list__carousel-slide';

          slide.dataset
            .listCarouselSlide =
            String(index + 1);

          const galleryImage =
            document.createElement('img');

          galleryImage.className =
            'the-list__image the-list__carousel-image';

          galleryImage.src =
            sizedImageUrl(
              image.src,
              900
            );

          galleryImage.srcset = [
            `${sizedImageUrl(
              image.src,
              600
            )} 600w`,
            `${sizedImageUrl(
              image.src,
              900
            )} 900w`,
            `${sizedImageUrl(
              image.src,
              1200
            )} 1200w`
          ].join(', ');

          galleryImage.sizes =
            window.matchMedia(
              '(max-width: 989px)'
            ).matches
              ? '80vw'
              : '50vw';

          galleryImage.alt =
            image.alt ||
            gallery.title ||
            '';

          galleryImage.loading = 'lazy';
          galleryImage.decoding = 'async';
          galleryImage.draggable = false;

          slide.append(galleryImage);
          track.append(slide);
        });

      galleryLength = Math.max(
        1,
        uniqueImages.length
      );

      createDots(galleryLength);
      goToSlide(0, 'auto');
    };

    const refreshGallery = async () => {
      const productHref =
        imageLink.href;

      const mainImageSource =
        primaryImage.currentSrc ||
        primaryImage.getAttribute(
          'src'
        ) ||
        '';

      if (
        !productHref ||
        !mainImageSource ||
        primaryImage.hidden
      ) {
        clearAdditionalSlides();
        return;
      }

      const currentVersion =
        ++galleryVersion;

      clearAdditionalSlides();

      carousel.classList.add(
        'is-loading'
      );

      try {
        const gallery =
          await loadProductGallery(
            productHref
          );

        if (
          currentVersion !==
          galleryVersion
        ) {
          return;
        }

        renderGallery(
          gallery,
          mainImageSource
        );
      } catch (error) {
        if (
          currentVersion ===
          galleryVersion
        ) {
          clearAdditionalSlides();

          console.error(
            'The List gallery failed:',
            error
          );
        }
      } finally {
        if (
          currentVersion ===
          galleryVersion
        ) {
          carousel.classList.remove(
            'is-loading'
          );
        }
      }
    };

    const scheduleGalleryRefresh = () => {
      if (pendingRefresh) return;

      pendingRefresh = true;

      queueMicrotask(() => {
        pendingRefresh = false;
        refreshGallery();
      });
    };

    previousButton.addEventListener(
      'click',
      () => {
        goToSlide(activeIndex - 1);
      }
    );

    nextButton.addEventListener(
      'click',
      () => {
        goToSlide(activeIndex + 1);
      }
    );

    track.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) {
          cancelAnimationFrame(
            scrollFrame
          );
        }

        scrollFrame =
          requestAnimationFrame(() => {
            scrollFrame = null;

            const slides = getSlides();

            if (!slides.length) return;

            const leadingPadding =
              Number.parseFloat(
                getComputedStyle(
                  track
                ).paddingLeft
              ) || 0;

            const currentPosition =
              track.scrollLeft +
              leadingPadding;

            let nearestIndex = 0;
            let nearestDistance =
              Infinity;

            slides.forEach(
              (slide, index) => {
                const distance =
                  Math.abs(
                    slide.offsetLeft -
                      currentPosition
                  );

                if (
                  distance <
                  nearestDistance
                ) {
                  nearestDistance =
                    distance;

                  nearestIndex = index;
                }
              }
            );

            activeIndex = nearestIndex;
            updateControls();
          });
      },
      { passive: true }
    );

    track.addEventListener(
      'pointerdown',
      (event) => {
        pointerStartX =
          event.clientX;

        suppressNextLinkClick = false;
      },
      { passive: true }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (pointerStartX === null) {
          return;
        }

        if (
          Math.abs(
            event.clientX -
              pointerStartX
          ) > 8
        ) {
          suppressNextLinkClick = true;
        }
      },
      { passive: true }
    );

    track.addEventListener(
      'pointerup',
      () => {
        pointerStartX = null;
      },
      { passive: true }
    );

    track.addEventListener(
      'pointercancel',
      () => {
        pointerStartX = null;
      },
      { passive: true }
    );

    imageLink.addEventListener(
      'click',
      (event) => {
        if (
          !suppressNextLinkClick
        ) {
          return;
        }

        event.preventDefault();
        suppressNextLinkClick = false;
      },
      true
    );

    const imageObserver =
      new MutationObserver(() => {
        scheduleGalleryRefresh();
      });

    imageObserver.observe(
      primaryImage,
      {
        attributes: true,
        attributeFilter: [
          'src',
          'srcset',
          'hidden'
        ]
      }
    );

    imageObserver.observe(
      imageLink,
      {
        attributes: true,
        attributeFilter: ['href']
      }
    );

    updateControls();

    if (
      primaryImage.getAttribute(
        'src'
      ) &&
      !primaryImage.hidden
    ) {
      scheduleGalleryRefresh();
    }
  };

  const initialiseAllGalleries = (
    root = document
  ) => {
    if (
      root instanceof Element &&
      root.matches(
        'the-list-browser'
      )
    ) {
      initialiseGallery(root);
    }

    root
      .querySelectorAll?.(
        'the-list-browser'
      )
      .forEach(initialiseGallery);
  };

  const start = () => {
    initialiseAllGalleries();

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        initialiseAllGalleries(
          event.target
        );
      }
    );

    const pageObserver =
      new MutationObserver(
        (mutations) => {
          mutations.forEach(
            (mutation) => {
              mutation.addedNodes.forEach(
                (node) => {
                  if (
                    node instanceof
                    Element
                  ) {
                    initialiseAllGalleries(
                      node
                    );
                  }
                }
              );
            }
          );
        }
      );

    pageObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
(() => {
  const installedBrowsers = new WeakSet();

  const mobileMedia = window.matchMedia(
    '(max-width: 989px)'
  );

  const getStableViewportWidth = () => {
    return Math.round(
      document.documentElement.clientWidth ||
      window.innerWidth ||
      0
    );
  };

  const installCarouselHeightLock = (
    browser
  ) => {
    if (
      !browser ||
      installedBrowsers.has(browser)
    ) {
      return;
    }

    const carousel = browser.querySelector(
      '[data-list-carousel]'
    );

    if (!carousel) return;

    installedBrowsers.add(browser);

    let lockedViewportWidth = null;
    let firstFrame = null;
    let secondFrame = null;

    const cancelFrames = () => {
      if (firstFrame) {
        cancelAnimationFrame(firstFrame);
        firstFrame = null;
      }

      if (secondFrame) {
        cancelAnimationFrame(secondFrame);
        secondFrame = null;
      }
    };

    const clearHeightLock = () => {
      cancelFrames();

      lockedViewportWidth = null;
      carousel.style.removeProperty(
        'height'
      );
    };

    const lockHeight = ({
      force = false
    } = {}) => {
      if (
        !mobileMedia.matches ||
        !browser.classList.contains(
          'is-sheet-open'
        )
      ) {
        clearHeightLock();
        return;
      }

      const currentViewportWidth =
        getStableViewportWidth();

      const widthChanged =
        lockedViewportWidth === null ||
        Math.abs(
          currentViewportWidth -
            lockedViewportWidth
        ) >= 2;

      /*
       * Ignore Safari address-bar changes because
       * they alter height without changing width.
       */
      if (!force && !widthChanged) {
        return;
      }

      cancelFrames();

      carousel.style.removeProperty(
        'height'
      );

      firstFrame =
        requestAnimationFrame(() => {
          firstFrame = null;

          secondFrame =
            requestAnimationFrame(() => {
              secondFrame = null;

              if (
                !mobileMedia.matches ||
                !browser.classList.contains(
                  'is-sheet-open'
                )
              ) {
                return;
              }

              const carouselHeight =
                carousel.getBoundingClientRect()
                  .height;

              if (carouselHeight <= 0) {
                return;
              }

              carousel.style.height =
                `${Math.round(
                  carouselHeight
                )}px`;

              lockedViewportWidth =
                currentViewportWidth;
            });
        });
    };

    const browserObserver =
      new MutationObserver(() => {
        if (
          browser.classList.contains(
            'is-sheet-open'
          )
        ) {
          clearHeightLock();

          lockHeight({
            force: true
          });
        } else {
          clearHeightLock();
        }
      });

    browserObserver.observe(browser, {
      attributes: true,
      attributeFilter: ['class']
    });

    const handleResize = () => {
      if (
        !mobileMedia.matches ||
        !browser.classList.contains(
          'is-sheet-open'
        )
      ) {
        return;
      }

      const currentViewportWidth =
        getStableViewportWidth();

      const widthChanged =
        lockedViewportWidth === null ||
        Math.abs(
          currentViewportWidth -
            lockedViewportWidth
        ) >= 2;

      if (!widthChanged) {
        return;
      }

      clearHeightLock();

      lockHeight({
        force: true
      });
    };

    window.addEventListener(
      'resize',
      handleResize,
      { passive: true }
    );

    window.visualViewport?.addEventListener(
      'resize',
      handleResize,
      { passive: true }
    );

    mobileMedia.addEventListener(
      'change',
      () => {
        clearHeightLock();

        if (
          mobileMedia.matches &&
          browser.classList.contains(
            'is-sheet-open'
          )
        ) {
          lockHeight({
            force: true
          });
        }
      }
    );

    if (
      browser.classList.contains(
        'is-sheet-open'
      )
    ) {
      lockHeight({
        force: true
      });
    }
  };

  const installAll = (
    root = document
  ) => {
    if (
      root instanceof Element &&
      root.matches('the-list-browser')
    ) {
      installCarouselHeightLock(root);
    }

    root
      .querySelectorAll?.(
        'the-list-browser'
      )
      .forEach(
        installCarouselHeightLock
      );
  };

  const start = () => {
    installAll();

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        installAll(event.target);
      }
    );

    const pageObserver =
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach(
            (node) => {
              if (
                !(node instanceof Element)
              ) {
                return;
              }

              installAll(node);

              const parentBrowser =
                node.closest?.(
                  'the-list-browser'
                );

              if (parentBrowser) {
                installCarouselHeightLock(
                  parentBrowser
                );
              }
            }
          );
        });
      });

    pageObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();

/*
 * Converts the gallery pagination into three position indicators:
 * beginning, middle and end.
 */
(() => {
  const CAROUSEL =
    '.the-list__carousel';

  const TRACK =
    '.the-list__carousel-track';

  const DOTS =
    '.the-list__carousel-dots';

  const POSITION =
    'data-carousel-position';

  const createDot = (
    name,
    label
  ) => {
    const dot =
      document.createElement('button');

    dot.type = 'button';
    dot.className =
      'the-list__carousel-dot';

    dot.setAttribute(
      POSITION,
      name
    );

    dot.setAttribute(
      'aria-label',
      label
    );

    return dot;
  };

  const ensureThreeDots = carousel => {
    const track =
      carousel.querySelector(TRACK);

    const container =
      carousel.querySelector(DOTS);

    if (!track || !container) {
      return null;
    }

    const currentDots =
      Array.from(
        container.querySelectorAll(
          `[${POSITION}]`
        )
      );

    if (currentDots.length === 3) {
      return {
        track,
        container,
        dots: currentDots
      };
    }

    const startDot = createDot(
      'start',
      'Go to first image'
    );

    const middleDot = createDot(
      'middle',
      'Go to middle images'
    );

    const endDot = createDot(
      'end',
      'Go to final image'
    );

    container.replaceChildren(
      startDot,
      middleDot,
      endDot
    );

    const dots = [
      startDot,
      middleDot,
      endDot
    ];

    dots.forEach(dot => {
      dot.addEventListener(
        'click',
        () => {
          const maximumScroll =
            Math.max(
              0,
              track.scrollWidth -
                track.clientWidth
            );

          const position =
            dot.getAttribute(POSITION);

          let destination = 0;

          if (position === 'middle') {
            destination =
              maximumScroll / 2;
          }

          if (position === 'end') {
            destination =
              maximumScroll;
          }

          track.scrollTo({
            left: destination,
            behavior: 'smooth'
          });
        }
      );
    });

    return {
      track,
      container,
      dots
    };
  };

  const updateDots = carousel => {
    const gallery =
      ensureThreeDots(carousel);

    if (!gallery) return;

    const {
      track,
      dots
    } = gallery;

    const maximumScroll =
      Math.max(
        0,
        track.scrollWidth -
          track.clientWidth
      );

    const progress =
      maximumScroll > 1
        ? track.scrollLeft /
          maximumScroll
        : 0;

    let currentPosition =
      'middle';

    if (progress <= 0.06) {
      currentPosition = 'start';
    } else if (progress >= 0.94) {
      currentPosition = 'end';
    }

    dots.forEach(dot => {
      const isActive =
        dot.getAttribute(POSITION) ===
        currentPosition;

      dot.classList.toggle(
        'is-active',
        isActive
      );

      dot.setAttribute(
        'aria-current',
        isActive ? 'true' : 'false'
      );
    });
  };

  const install = carousel => {
    const gallery =
      ensureThreeDots(carousel);

    if (!gallery) return;

    const { track } = gallery;

    if (
      track.dataset
        .threeDotsInstalled !== 'true'
    ) {
      track.dataset
        .threeDotsInstalled = 'true';

let scrollTimer = null;

track.addEventListener(
  'scroll',
  () => {
    window.clearTimeout(
      scrollTimer
    );

    scrollTimer =
      window.setTimeout(() => {
        updateDots(carousel);
      }, 140);
  },
  { passive: true }
);

      new ResizeObserver(() => {
        updateDots(carousel);
      }).observe(track);
    }

    updateDots(carousel);
  };

  const scan = root => {
    if (
      root instanceof Element &&
      root.matches(CAROUSEL)
    ) {
      install(root);
    }

    root
      .querySelectorAll?.(CAROUSEL)
      .forEach(install);
  };

  const initialise = () => {
    scan(document);

    new MutationObserver(
      mutations => {
        mutations.forEach(mutation => {
          const carousel =
            mutation.target instanceof
              Element
              ? mutation.target.closest(
                  CAROUSEL
                )
              : null;

          if (carousel) {
            install(carousel);
          }

          mutation.addedNodes.forEach(
            node => {
              if (node instanceof Element) {
                scan(node);
              }
            }
          );
        });
      }
    ).observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      { once: true }
    );
  } else {
    initialise();
  }
})();